<template>
  <div
    v-if="loading || realtimeLoading"
    class="h-screen bg-white bg-opacity-75 flex justify-center items-center z-10"
  >
    <loading
      loading-text="Yükleniyor"
      :show-loading-text="true"
    />
  </div>
  <div v-else class="dashboard space-y-4">
    <!-- Analytics Kontrol Paneli - Sadece mülk bağlıysa göster -->
    <div
      v-if="isConnected && isAuthorized && hasPropertyId"
      class="bg-white p-4 rounded-lg shadow-sm flex items-center justify-between"
    >
      <div class="flex items-center gap-4">
        <!-- Tab switching buttons -->
        <div class="bg-gray-100 rounded-lg p-1 flex">
          <button
            @click="switchTab('historical')"
            class="px-4 py-1.5 rounded-md transition w-60 text-center font-medium"
            :class="
              activeTab === 'historical'
                ? 'bg-primary text-white shadow-sm'
                : 'text-gray-700 hover:bg-gray-200 hover:text-gray-900'
            "
          >
            Tarihsel Veriler
          </button>
          <button
            @click="switchTab('realtime')"
            class="px-4 py-1.5 rounded-md transition w-60 text-center font-medium"
            :class="
              activeTab === 'realtime'
                ? 'bg-primary text-white shadow-sm'
                : 'text-gray-700 hover:bg-gray-200 hover:text-gray-900'
            "
          >
            Gerçek Zamanlı Veriler
          </button>
        </div>
      </div>

      <div class="flex items-center space-x-2">
        <!-- Date Range Picker (only for historical tab) -->
        <template v-if="activeTab === 'historical'">
          <div class="w-64">
            <VDateRangePicker v-model="dateRange" placeholder="Tarih Seçin" />
          </div>
          <button
            v-if="dateRange.start && dateRange.end"
            @click="clearDateRange"
            class="ml-2 p-1 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100 transition"
            title="Tarihi sıfırla"
          >
            <span class="material-symbols-outlined" style="font-size: 18px"
              >close</span
            >
          </button>
        </template>
      </div>
    </div>

    <div
      v-if="!isConnected || !isAuthorized || !hasPropertyId"
      class="flex flex-col items-center justify-center min-h-[400px] p-8 bg-white rounded-lg shadow-sm"
    >
      <!-- Görsel alanı -->
      <div class="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-6">
        <span class="material-symbols-outlined text-primary text-4xl">{{ authMessage.icon }}</span>
      </div>
      
      <!-- Metin alanı -->
      <div class="text-center space-y-3 mb-8">
        <h3 class="text-xl font-semibold text-gray-900">
          {{ authMessage.title }}
        </h3>
        <p class="text-gray-600 max-w-md mx-auto">{{ authMessage.description }}</p>
      </div>

      <!-- Buton alanı -->
      <div class="flex gap-4">
        <custom-button
          button-text="Ayarlara Git"
          color="primary"
          size="md"
          :show-icon="true"
          icon="settings"
          @click="navigateToIntegrations"
        />
      </div>
    </div>

    <template v-else>
      <!-- Historical Data Tab -->
      <div v-if="activeTab === 'historical'">
        <div class="p-4 component-layout">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <MetricCard
              title="Toplam Ziyaretçi"
              :value="processedData.totals?.activeUsers ?? 0"
              :change="0"
              :loading="loading"
            />
            <MetricCard
              title="Toplam Oturum"
              :value="processedData.totals?.sessions ?? 0"
              :change="0"
              :loading="loading"
            />
            <MetricCard
              title="Hemen Çıkma Oranı"
              :value="formatBounceRate(processedData.totals?.bounceRate)"
              :change="0"
              :loading="loading"
            />
          </div>
        </div>

        <VisitorChart
          title="Ziyaretçi İstatistikleri"
          :data="visitorChartData"
          :loading="loading"
        />

        <div class="component-layout">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <CountryChart
              title="Ülkelere Göre Dağılım"
              :data="countryChartData"
              :loading="loading"
            />
            <DeviceChart
              title="Cihazlara Göre Dağılım"
              :data="deviceChartData"
              :loading="loading"
            />
          </div>
        </div>

        <div class="component-layout">
          <SourceTable
            title="Trafik Kaynakları"
            :data="sourceTableData"
            :loading="loading"
          />
        </div>
      </div>

      <!-- Realtime Data Tab -->
      <div v-else>
        <div class="p-4 component-layout">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <MetricCard
              title="Aktif Kullanıcılar"
              :value="realtimeData.totals?.activeUsers ?? 0"
              :loading="realtimeLoading"
              icon="person"
            />
            <MetricCard
              title="Sayfa Görüntülemeleri"
              :value="realtimeData.totals?.screenPageViews ?? 0"
              :loading="realtimeLoading"
              icon="visibility"
            />
            <MetricCard
              title="Olay Sayısı"
              :value="realtimeData.totals?.eventCount ?? 0"
              :loading="realtimeLoading"
              icon="touch_app"
            />
          </div>
        </div>

        <VisitorChart
          title="Gerçek Zamanlı Ziyaretçiler"
          :data="realtimeTimelineData"
          :loading="realtimeLoading"
          :realtime="true"
        />

        <div class="component-layout">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <CountryChart
              title="Ülkelere Göre Dağılım"
              :data="realtimeCountryData"
              :loading="realtimeLoading"
            />
            <DeviceChart
              title="Cihazlara Göre Dağılım"
              :data="realtimeDeviceData"
              :loading="realtimeLoading"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
// Composable
import { useAnalytics } from "./composables";


// Components
import VisitorChart from "./components/visitorChart.vue";
import MetricCard from "./components/metricCard.vue";
import CountryChart from "./components/countryChart.vue";
import DeviceChart from "./components/deviceChart.vue";
import SourceTable from "./components/sourceTable.vue";
import CustomButton from "@/components/base/buttons/VButton.vue";
import VDateRangePicker from "@/components/base/inputs/VDateRangePicker.vue";
import Loading from "../../../../components/loading/VLoading.vue";

// Store
import { useAssetStore } from "@/stores/asset";

// Asset ID
const assetStore = useAssetStore();
const assetId = assetStore.assetId;

// Analytics composable kullanımı
const {
  // State
  activeTab,
  dateRange,
  isConnected,
  isAuthorized,
  hasPropertyId,
  processedData,
  realtimeData,
  loading,
  realtimeLoading,
  authMessage,

  // Computed properties
  visitorChartData,
  countryChartData,
  deviceChartData,
  sourceTableData,
  realtimeTimelineData,
  realtimeCountryData,
  realtimeDeviceData,

  // Formatters
  formatBounceRate,

  // Methods
  switchTab,
  clearDateRange,
  navigateToIntegrations,
} = useAnalytics(assetId);
</script>

<style scoped>
.component-layout {
  @apply bg-white p-4 rounded-lg shadow-sm;
}
</style>
